/**
 * storage.js — Wrapper localStorage typé FG-OS
 *
 * Toutes les donnees terrain (prospects, audits, drafts, theme, CA hors-FG)
 * sont persistees en localStorage. Mode hors-ligne possible : Wissam peut
 * auditer un parc en magasin sans wifi, sync au retour bureau.
 *
 * Namespace : "fg-os:" sur toutes les cles pour eviter collisions avec
 * d'autres apps si jamais le domaine est partage.
 *
 * Versioning : chaque entree est wrappee { v: STORAGE_VERSION, data: ... }
 * pour permettre une migration future sans casser les donnees existantes.
 *
 * SSR-safe : tous les acces localStorage sont gardes par typeof window.
 * Mode incognito / quota exceeded : try/catch silent, retour fallback.
 */

import { hasFutureProchaineAction, resolveProchaineAction } from './interactions.js';

// ============================================================
// Constants
// ============================================================

/** Prefixe sur toutes les cles localStorage de FG-OS. */
const NAMESPACE = 'fg-os:';

/**
 * Version courante du schema de stockage.
 * Incrementer ici quand on change la structure des donnees stockees.
 * La fonction get() peut alors detecter la version et migrer si necessaire.
 */
export const STORAGE_VERSION = 1;

/**
 * Cles canoniques utilisees dans FG-OS. Centralisees pour eviter typos.
 */
export const KEYS = Object.freeze({
  PROSPECTS:    'prospects',
  AUDITS:       'audits',           // index : array de { id, clientName, createdAt }
  AUDIT_DATA:   'audit:',           // prefixe + auditId, ex: "audit:<uuid>"
  ACTIVE_AUDIT: 'active-audit-id',  // audit courant pour pont Audit -> Chiffrage
  ACTIVE_PROSPECT_FOR_AUDIT: 'active-prospect-for-audit-id', // pont Pipeline -> Audit (pre-remplir client)
  ACTIVE_NAV:   'active-nav-id',    // dernier onglet actif (persist reload)
  SIRENE_IMPORTED: 'sirene-imported',          // SIRETs deja importes en Pipeline (anti-doublon)
  SIRENE_IMPORT_HISTORY: 'sirene-import-history', // historique imports SIRENE
  ENRICH_DATA: 'enrich:',                      // prefixe + siret : cache enrichissement 7j
  DEVIS:        'devis',            // index : array de { id, numero, clientName, statut, total, createdAt, updatedAt }
  DEVIS_DATA:   'devis:',           // prefixe + devisId pour detail complet
  ACTIVE_DEVIS: 'active-devis-id',  // devis courant pour pont Chiffrage -> Pipeline (futur)
  DRAFTS:       'drafts',           // brouillons mails generes avant envoi Gmail
  THEME:        'theme',            // "light" | "dark"
  CA_NON_FG:    'ca-non-fg',        // CA mensuel global hors-FG (Wissam saisit en Reglages)
  DEVIS_COUNTER:'devis-counter',    // compteur incremental pour numero DEV-2026-NNNN
  LAST_SYNC:    'last-sync',        // timestamp ISO derniere sync Notion reussie
  TARIF_MOIS:   'tarif-mois',       // mois courant pour reset counter automatique
  NOTIFICATIONS: 'notifications',   // liste notifications generees (Bloc 1)
  MAILS_GENERES: 'mails-generes',   // historique 5 derniers mails (Module 6)
  INKYA_CATALOGUE: 'inkya-catalogue', // catalogue concurrent imprim-encre.com (prix client final TTC)
  INKYA_META:      'inkya-meta',       // { importedAt, count } du dernier import Inkya
  COMMANDES:       'commandes',        // historique d'achat reel par prospect (SPEC-04, socle reassort)
  INTERACTIONS:    'interactions',     // journal appel/mail/visite/note par prospect (SPEC-05)
});

// ============================================================
// Acces bas niveau (private helpers + API exposee)
// ============================================================

/**
 * Verifie que localStorage est disponible et utilisable.
 * Detecte SSR, navigateurs sans support, mode incognito Safari (quota = 0).
 */
function isAvailable() {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  try {
    const probe = `${NAMESPACE}__probe__`;
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

/**
 * Lit une cle namespacee. Retourne fallback si :
 *   - localStorage indisponible
 *   - cle absente
 *   - JSON malforme
 *   - version incompatible (non migree)
 *
 * @template T
 * @param {string} key  cle (sans le prefixe NAMESPACE)
 * @param {T} [fallback=null]
 * @returns {T}
 */
export function get(key, fallback = null) {
  if (!isAvailable()) return fallback;
  try {
    const raw = window.localStorage.getItem(NAMESPACE + key);
    if (raw === null) return fallback;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !('v' in parsed) || !('data' in parsed)) {
      console.warn(`[storage] format invalide pour "${key}", fallback`);
      return fallback;
    }
    if (parsed.v > STORAGE_VERSION) {
      console.warn(`[storage] "${key}" version ${parsed.v} > current ${STORAGE_VERSION}, fallback`);
      return fallback;
    }
    // Migration future : si parsed.v < STORAGE_VERSION, appeler migrate(parsed)
    return parsed.data;
  } catch (err) {
    console.warn(`[storage] erreur lecture "${key}":`, err.message);
    return fallback;
  }
}

/**
 * Ecrit une cle namespacee. Wrap auto en { v: STORAGE_VERSION, data: value }.
 * Retourne true si OK, false si echec (quota, indispo).
 *
 * @param {string} key
 * @param {any} value JSON-serialisable
 * @returns {boolean}
 */
export function set(key, value) {
  if (!isAvailable()) return false;
  try {
    const wrapped = { v: STORAGE_VERSION, data: value };
    window.localStorage.setItem(NAMESPACE + key, JSON.stringify(wrapped));
    return true;
  } catch (err) {
    console.warn(`[storage] erreur ecriture "${key}":`, err.message);
    return false;
  }
}

/**
 * Supprime une cle namespacee. Idempotent.
 */
export function remove(key) {
  if (!isAvailable()) return;
  try {
    window.localStorage.removeItem(NAMESPACE + key);
  } catch {
    /* ignore */
  }
}

/**
 * Reset complet : supprime toutes les cles fg-os:* du localStorage.
 * Utilise par le Module Reglages > Reset donnees locales.
 *
 * @returns {number} nombre de cles supprimees
 */
export function clearAll() {
  if (!isAvailable()) return 0;
  try {
    const toRemove = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(NAMESPACE)) toRemove.push(k);
    }
    for (const k of toRemove) window.localStorage.removeItem(k);
    return toRemove.length;
  } catch {
    return 0;
  }
}

/**
 * Retourne la taille totale (octets approx) occupee par FG-OS dans localStorage.
 * Utile pour le Module Reglages : afficher l'empreinte data locale.
 */
export function approxSize() {
  if (!isAvailable()) return 0;
  let bytes = 0;
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(NAMESPACE)) {
        bytes += k.length + (window.localStorage.getItem(k)?.length || 0);
      }
    }
  } catch {
    /* ignore */
  }
  return bytes * 2; // UTF-16 = 2 octets par char (approx)
}

// ============================================================
// Helpers metier — Prospects
// ============================================================

/**
 * Structure prospect :
 * {
 *   id: string (UUID v4),
 *   nom: string,                       // nom client / entreprise
 *   type: 'interne' | 'externe',       // interne GMS bureau / externe B2B
 *   statut: 'repere' | 'audite' | 'devis_envoye' | 'compte_ouvert' | 'recurrent',
 *   contact: string,                   // tel, email, nom personne
 *   notes: string,
 *   caEstimeParcMensuel: number,       // CA HT parc estime / mois
 *   dateContact: string (ISO),         // dernier contact
 *   dateAjout: string (ISO),
 *   dateDernierStatut: string (ISO),
 *   relanceJ5: string | null (ISO),    // calculee J+5 apres "devis_envoye"
 *   reseau: boolean,                   // tag reseau/franchise (SPEC-03, debloque Phase 3 scoring)
 *   ton: 'vouvoiement' | 'tutoiement', // ton mail memorise (SPEC-03) — absent = defaut derive
 *   tonForce: boolean,                 // true = choisi manuellement par Wissam, le defaut ne l'ecrase plus
 * }
 * Champs SPEC-03 retrocompatibles : absents sur les prospects existants,
 * defaut applique a la volee par deriveTon()/deriveSegment(), pas de migration.
 */

/** Valeurs valides du ton mail. */
export const TON_VALUES = Object.freeze(['vouvoiement', 'tutoiement']);

/**
 * Segment commercial derive : reseau/franchise > GMS interne > B2B externe.
 * @returns {'gms_interne' | 'b2b_externe' | 'reseau'}
 */
export function deriveSegment(p) {
  if (p && p.reseau === true) return 'reseau';
  return p && p.type === 'interne' ? 'gms_interne' : 'b2b_externe';
}

/**
 * Ton effectif d'un prospect (SPEC-03).
 * Si Wissam a force manuellement (tonForce), son choix est respecte.
 * Sinon defaut : GMS interne avec compte ouvert/recurrent -> tutoiement
 * (relation terrain etablie) ; tout le reste -> vouvoiement.
 * @returns {'vouvoiement' | 'tutoiement'}
 */
export function deriveTon(p) {
  if (p && p.tonForce === true && TON_VALUES.includes(p.ton)) return p.ton;
  const segment = deriveSegment(p);
  if (segment === 'gms_interne' && p && (p.statut === 'compte_ouvert' || p.statut === 'recurrent')) {
    return 'tutoiement';
  }
  return 'vouvoiement';
}

/**
 * Force le ton d'un prospect (choix manuel Wissam). Pose tonForce=true :
 * le defaut automatique ne l'ecrasera plus.
 */
export function setProspectTon(id, ton) {
  if (!TON_VALUES.includes(ton)) {
    console.warn(`[storage] setProspectTon: ton invalide "${ton}"`);
    return null;
  }
  return updateProspect(id, { ton, tonForce: true });
}

/**
 * Repasse le prospect en ton automatique (deriveTon reprend la main).
 */
export function clearProspectTonForce(id) {
  return updateProspect(id, { tonForce: false });
}

export function getProspects() {
  return get(KEYS.PROSPECTS, []);
}

export function setProspects(prospects) {
  return set(KEYS.PROSPECTS, prospects);
}

export function addProspect(prospect) {
  const list = getProspects();
  const enriched = {
    ...prospect,
    id: prospect.id || crypto.randomUUID(),
    dateAjout: prospect.dateAjout || new Date().toISOString(),
    dateDernierStatut: new Date().toISOString(),
  };
  list.push(enriched);
  setProspects(list);
  return enriched;
}

export function updateProspect(id, patch) {
  const list = getProspects();
  const idx = list.findIndex((p) => p.id === id);
  if (idx === -1) return null;
  const updated = {
    ...list[idx],
    ...patch,
    id, // id immuable
    dateDernierStatut: patch.statut && patch.statut !== list[idx].statut
      ? new Date().toISOString()
      : list[idx].dateDernierStatut,
  };
  list[idx] = updated;
  setProspects(list);
  return updated;
}

export function removeProspect(id) {
  const all = getProspects();
  const target = all.find((p) => p.id === id);
  const list = all.filter((p) => p.id !== id);
  setProspects(list);
  // Sync : si prospect importe via SIRENE, retire son siret du set des importes
  // pour que Module 8 Prospection puisse le re-importer si besoin
  if (target?.siret) {
    removeImportedSiret(target.siret);
  }
}

export function getProspect(id) {
  return getProspects().find((p) => p.id === id) || null;
}

// ============================================================
// Helpers metier — Audits parc
// ============================================================

/**
 * Structure audit :
 * {
 *   id: string (UUID v4),
 *   clientName: string,
 *   prospectId: string | null,        // lien optionnel vers un prospect
 *   imprimantes: Array<{
 *     id: string,
 *     modele: string,                 // saisie brute Wissam
 *     matches: Array<...> | [],       // resultat matchPrinterModel
 *     uncoveredBrand: string | null,  // xerox/ricoh/oki si non couvert
 *     notes: string,
 *   }>,
 *   createdAt: string (ISO),
 *   updatedAt: string (ISO),
 * }
 *
 * Stockage : index dans KEYS.AUDITS (array { id, clientName, createdAt }),
 * detail individuel dans KEYS.AUDIT_DATA + id (acces O(1) sans charger tout).
 */

export function listAudits() {
  return get(KEYS.AUDITS, []);
}

export function getAudit(auditId) {
  return get(KEYS.AUDIT_DATA + auditId, null);
}

export function saveAudit(audit) {
  const id = audit.id || crypto.randomUUID();
  const now = new Date().toISOString();
  const enriched = {
    ...audit,
    id,
    createdAt: audit.createdAt || now,
    updatedAt: now,
  };
  // detail
  set(KEYS.AUDIT_DATA + id, enriched);
  // index — prospectId ajoute (SPEC-03) pour rattacher un audit a un prospect
  // sans charger tous les details. Nouveaux audits seulement : les entrees
  // anciennes n'ont pas ce champ (pas de migration retroactive — un audit
  // ancien non injecte dans un mail n'est pas grave, un montant invente si).
  const index = listAudits();
  const existing = index.findIndex((a) => a.id === id);
  const entry = { id, clientName: enriched.clientName, createdAt: enriched.createdAt, prospectId: enriched.prospectId || null };
  if (existing === -1) {
    index.push(entry);
  } else {
    index[existing] = entry;
  }
  set(KEYS.AUDITS, index);
  return enriched;
}

export function removeAudit(auditId) {
  remove(KEYS.AUDIT_DATA + auditId);
  const index = listAudits().filter((a) => a.id !== auditId);
  set(KEYS.AUDITS, index);
}

// ============================================================
// Helpers metier — Drafts mails
// ============================================================

/**
 * Structure draft :
 * {
 *   id: string (UUID v4),
 *   prospectId: string | null,
 *   type: 'relance' | 'proposition_audit' | 'autre',
 *   subject: string,
 *   body: string,
 *   createdAt: string (ISO),
 *   sentToGmail: boolean,             // true si brouillon Gmail cree avec succes
 *   gmailDraftId: string | null,
 * }
 */

export function getDrafts() {
  return get(KEYS.DRAFTS, []);
}

export function addDraft(draft) {
  const list = getDrafts();
  const enriched = {
    ...draft,
    id: draft.id || crypto.randomUUID(),
    createdAt: draft.createdAt || new Date().toISOString(),
    sentToGmail: !!draft.sentToGmail,
    gmailDraftId: draft.gmailDraftId || null,
  };
  list.push(enriched);
  set(KEYS.DRAFTS, list);
  return enriched;
}

export function updateDraft(id, patch) {
  const list = getDrafts();
  const idx = list.findIndex((d) => d.id === id);
  if (idx === -1) return null;
  list[idx] = { ...list[idx], ...patch, id };
  set(KEYS.DRAFTS, list);
  return list[idx];
}

export function removeDraft(id) {
  set(KEYS.DRAFTS, getDrafts().filter((d) => d.id !== id));
}

// ============================================================
// Helpers metier — Theme
// ============================================================

/**
 * Retourne le theme stocke ('light' | 'dark') ou null si jamais defini.
 */
export function getTheme() {
  return get(KEYS.THEME, null);
}

/**
 * Persiste le theme. A coupler avec applyTheme() de tokens.js.
 */
export function setTheme(theme) {
  if (theme !== 'light' && theme !== 'dark') {
    console.warn(`[storage] setTheme: valeur invalide "${theme}"`);
    return false;
  }
  return set(KEYS.THEME, theme);
}

// ============================================================
// Helpers metier — CA hors-FG (Reglages)
// ============================================================

/**
 * Wissam saisit dans Reglages son CA mensuel global non-FG (ventes GMS classiques).
 * Le CA FG (issu des devis valides en pipeline) s'ajoute pour le calcul des paliers.
 *
 * Structure : { mois: 'YYYY-MM', montant: number }
 */
export function getCANonFG() {
  return get(KEYS.CA_NON_FG, { mois: null, montant: 0 });
}

export function setCANonFG(montant, mois) {
  const currentMonth = mois || new Date().toISOString().slice(0, 7); // "YYYY-MM"
  return set(KEYS.CA_NON_FG, { mois: currentMonth, montant: Number(montant) || 0 });
}

// ============================================================
// Helpers metier — Numero devis incremental DEV-YYYY-NNNN
// ============================================================

/**
 * Genere le prochain numero de devis au format DEV-YYYY-NNNN.
 * Compteur reset automatiquement au changement d'annee.
 *
 * @returns {string} ex: "DEV-2026-0042"
 */
export function nextDevisNumber() {
  const now = new Date();
  const year = now.getFullYear();
  const state = get(KEYS.DEVIS_COUNTER, { year: null, n: 0 });
  const n = state.year === year ? state.n + 1 : 1;
  set(KEYS.DEVIS_COUNTER, { year, n });
  return `DEV-${year}-${String(n).padStart(4, '0')}`;
}

/**
 * Lecture seule du dernier numero genere (sans incrementer).
 */
export function peekDevisCounter() {
  return get(KEYS.DEVIS_COUNTER, { year: null, n: 0 });
}

// ============================================================
// Helpers metier — Last sync Notion (Module 2)
// ============================================================

export function getLastSync() {
  return get(KEYS.LAST_SYNC, null);
}

export function setLastSync(timestamp) {
  return set(KEYS.LAST_SYNC, timestamp || new Date().toISOString());
}

// ============================================================
// Helpers metier — Active Audit (pont Module 3 -> Module 4)
// ============================================================

/**
 * ID de l'audit "actif" dont les refs selectionnees doivent etre
 * pre-chargees dans le Module 4 Chiffrage.
 *
 * Set par Module 3 quand Wissam clique "Generer devis" depuis un audit
 * sauvegarde. Read par Module 4 au mount, puis clear apres consommation
 * (sinon devis vide rechargerait l'audit en boucle).
 *
 * @returns {string | null}
 */
export function getActiveAuditId() {
  return get(KEYS.ACTIVE_AUDIT, null);
}

/**
 * Persist l'ID de l'audit actif. Use cuando user clique "Generer devis"
 * dans Module 3 juste avant de switcher vers Module 4.
 *
 * @param {string} auditId
 * @returns {boolean}
 */
export function setActiveAuditId(auditId) {
  if (typeof auditId !== 'string' || !auditId) {
    console.warn('[storage] setActiveAuditId: auditId invalide');
    return false;
  }
  return set(KEYS.ACTIVE_AUDIT, auditId);
}

/**
 * Supprime l'ID actif. Use par Module 4 apres avoir charge l'audit,
 * pour eviter re-chargement automatique au prochain mount Chiffrage.
 */
export function clearActiveAuditId() {
  remove(KEYS.ACTIVE_AUDIT);
}

// ============================================================
// Helpers metier — Active Nav (persist dernier onglet)
// ============================================================

/**
 * Retourne l'ID du dernier onglet actif (ou null si premier load).
 * Use par NavContext au mount pour restaurer l'etat post-reload.
 *
 * @returns {string | null}
 */
export function getActiveNav() {
  return get(KEYS.ACTIVE_NAV, null);
}

/**
 * Persist l'ID de l'onglet actif. Validation generique (string non-vide).
 * La validation metier (ID = module connu) est faite cote App.jsx
 * pour garder storage.js agnostique.
 *
 * @param {string} navId
 * @returns {boolean}
 */
export function setActiveNav(navId) {
  if (typeof navId !== 'string' || !navId) {
    console.warn('[storage] setActiveNav: navId invalide');
    return false;
  }
  return set(KEYS.ACTIVE_NAV, navId);
}

// ============================================================
// Helpers metier — Devis (Module 4 Chiffrage)
// ============================================================

/**
 * Structure devis persistant (cf plan Module 4) :
 * {
 *   id: string (UUID v4),
 *   numero: string | null,      // "DEV-2026-0042", assigne au 1er save
 *   statut: 'brouillon' | 'envoye' | 'accepte' | 'refuse',
 *   clientName: string,
 *   clientAdresse: string,
 *   clientContact: string,
 *   prospectId: string | null,
 *   auditId: string | null,
 *   lignes: [{
 *     id, ref_interne, designation, type, couleur, pa_ht, quantite
 *   }],
 *   sousTotal, port, total: number,       // snapshots
 *   prixAmazon, economie, pourcentage: number,
 *   perteVsAmazon: boolean,
 *   notes: string,                         // internes Wissam, hors PDF
 *   validite: number,                      // jours, default 30
 *   createdAt, updatedAt: ISO,
 *   dateEnvoi, dateAcceptation, dateRefus: ISO | null,
 * }
 *
 * Pattern stockage : index leger dans KEYS.DEVIS, detail individuel dans
 * KEYS.DEVIS_DATA + devisId (acces O(1) sans desserialiser tout l'index).
 */

/**
 * Liste l'index des devis : array de { id, numero, clientName, statut, total, createdAt, updatedAt }.
 * Utilise pour AccueilChiffrage liste recente + Pipeline (futur).
 */
export function listDevis() {
  return get(KEYS.DEVIS, []);
}

/**
 * Detail complet d'un devis.
 * @returns {object | null}
 */
export function getDevis(devisId) {
  return get(KEYS.DEVIS_DATA + devisId, null);
}

/**
 * Persist un devis (create ou update).
 * Au 1er save : si numero null, assigne via nextDevisNumber().
 * Met a jour l'index + le detail.
 *
 * @returns devis enrichi (avec id, numero, createdAt, updatedAt)
 */
export function saveDevis(devis) {
  if (!devis || typeof devis !== 'object') {
    console.warn('[storage] saveDevis: devis invalide');
    return null;
  }

  const id = devis.id || (typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `d-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const now = new Date().toISOString();

  // Assignation numero au 1er save
  let numero = devis.numero;
  if (!numero) {
    numero = nextDevisNumber();
  }

  const enriched = {
    ...devis,
    id,
    numero,
    statut: devis.statut || 'brouillon',
    createdAt: devis.createdAt || now,
    updatedAt: now,
  };

  // Detail
  set(KEYS.DEVIS_DATA + id, enriched);

  // Index update (inclut prospectId + auditId pour filtres rapides
  // sans avoir a desserialiser tous les details)
  const index = listDevis();
  const existingIdx = index.findIndex((d) => d.id === id);
  const entry = {
    id,
    numero,
    clientName: enriched.clientName,
    statut: enriched.statut,
    total: enriched.total,
    prospectId: enriched.prospectId || null,
    auditId: enriched.auditId || null,
    createdAt: enriched.createdAt,
    updatedAt: now,
  };
  if (existingIdx === -1) {
    index.push(entry);
  } else {
    index[existingIdx] = entry;
  }
  set(KEYS.DEVIS, index);

  return enriched;
}

/**
 * Supprime un devis (detail + entree index). Idempotent.
 */
export function removeDevis(devisId) {
  remove(KEYS.DEVIS_DATA + devisId);
  set(KEYS.DEVIS, listDevis().filter((d) => d.id !== devisId));
}

/**
 * Filtre l'index par statut. Utile pour Pipeline (devis envoyes en attente, etc.).
 * @param {'brouillon' | 'envoye' | 'accepte' | 'refuse'} statut
 */
export function listDevisByStatut(statut) {
  return listDevis().filter((d) => d.statut === statut);
}

/**
 * Filtre l'index par prospect lie. Utile pour fiche prospect (futur).
 */
export function listDevisByProspect(prospectId) {
  return listDevis().filter((d) => d.prospectId === prospectId);
}

/**
 * Active devis : pont Chiffrage -> Pipeline / autre module futur.
 * Pas utilise au MVP Module 4 mais reserve pour cohérence avec ACTIVE_AUDIT.
 */
export function getActiveDevisId() {
  return get(KEYS.ACTIVE_DEVIS, null);
}

export function setActiveDevisId(devisId) {
  if (typeof devisId !== 'string' || !devisId) {
    console.warn('[storage] setActiveDevisId: devisId invalide');
    return false;
  }
  return set(KEYS.ACTIVE_DEVIS, devisId);
}

export function clearActiveDevisId() {
  remove(KEYS.ACTIVE_DEVIS);
}

// ============================================================
// Helpers metier — Active Prospect for Audit (pont Pipeline -> Audit)
// ============================================================

export function getActiveProspectForAudit() {
  return get(KEYS.ACTIVE_PROSPECT_FOR_AUDIT, null);
}

export function setActiveProspectForAudit(prospectId) {
  if (typeof prospectId !== 'string' || !prospectId) {
    console.warn('[storage] setActiveProspectForAudit: prospectId invalide');
    return false;
  }
  return set(KEYS.ACTIVE_PROSPECT_FOR_AUDIT, prospectId);
}

export function clearActiveProspectForAudit() {
  remove(KEYS.ACTIVE_PROSPECT_FOR_AUDIT);
}

// ============================================================
// Helpers metier — SIRENE (Module 8 Prospection)
// ============================================================

/**
 * Retourne la liste des SIRETs deja importes en Pipeline (anti-doublon).
 * @returns {string[]}
 */
export function getImportedSirets() {
  return get(KEYS.SIRENE_IMPORTED, []);
}

/**
 * Ajoute un SIRET a la liste des importes. Idempotent (no-op si deja present).
 * @param {string} siret
 * @returns {boolean} true si ajoute, false si deja present
 */
export function addImportedSiret(siret) {
  if (!siret || typeof siret !== 'string') return false;
  const list = getImportedSirets();
  if (list.includes(siret)) return false;
  list.push(siret);
  set(KEYS.SIRENE_IMPORTED, list);
  return true;
}

/**
 * Test rapide via Set (O(1) sur lookup multiple).
 * @param {string} siret
 * @returns {boolean}
 */
export function isAlreadyImported(siret) {
  return getImportedSirets().includes(siret);
}

/**
 * Retire un SIRET de la liste des importes. Idempotent (no-op si absent).
 * Appele auto depuis removeProspect() quand le prospect a un siret.
 * @returns {boolean} true si retire, false si etait absent
 */
export function removeImportedSiret(siret) {
  if (!siret) return false;
  const list = getImportedSirets();
  const next = list.filter((s) => s !== siret);
  if (next.length === list.length) return false;
  set(KEYS.SIRENE_IMPORTED, next);
  return true;
}

/**
 * Nettoyage : retire de SIRENE_IMPORTED les SIRETs orphelins
 * (= ceux pour lesquels aucun prospect avec ce siret n'existe).
 * Appelable au mount Module 8 et Module 5 pour garantir sync.
 *
 * @returns {number} nombre de SIRETs orphelins retires
 */
export function cleanupOrphanImportedSirets() {
  const imported = getImportedSirets();
  if (imported.length === 0) return 0;
  const prospects = getProspects();
  const validSirets = new Set(prospects.map((p) => p.siret).filter(Boolean));
  const cleaned = imported.filter((s) => validSirets.has(s));
  const removed = imported.length - cleaned.length;
  if (removed > 0) {
    set(KEYS.SIRENE_IMPORTED, cleaned);
  }
  return removed;
}

/**
 * Historique des imports SIRENE : array de { date, departement, nafCode, nbAjoutes }.
 */
export function getSireneImportHistory() {
  return get(KEYS.SIRENE_IMPORT_HISTORY, []);
}

export function addSireneImportEntry(entry) {
  const list = getSireneImportHistory();
  list.unshift({
    date: new Date().toISOString(),
    ...entry,
  });
  // Garder les 50 derniers
  set(KEYS.SIRENE_IMPORT_HISTORY, list.slice(0, 50));
}

// ============================================================
// Helpers metier — Enrichissement prospects (cache 7j)
// ============================================================

const ENRICH_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 jours

/**
 * Recupere l'enrichissement cache pour un SIRET. Retourne null si absent
 * ou expire (> 7 jours).
 */
export function getEnrichissement(siret) {
  if (!siret) return null;
  const data = get(KEYS.ENRICH_DATA + siret, null);
  if (!data) return null;
  if (data.cachedAt && Date.now() - new Date(data.cachedAt).getTime() > ENRICH_TTL_MS) {
    return null;
  }
  return data;
}

/**
 * Test si un enrichissement est present ET frais (< 7j).
 */
export function isEnrichmentFresh(siret) {
  return getEnrichissement(siret) !== null;
}

/**
 * Persiste un enrichissement. Ajoute cachedAt + siret au payload pour TTL check.
 *
 * @param {string} siret
 * @param {object} data { dirigeant, telephone, email, formeJuridique, dateCreation,
 *                        capitalSocial, sourceEnrichissement, pitch?, objections? }
 * @returns enrichissement enrichi
 */
export function saveEnrichissement(siret, data) {
  if (!siret) return null;
  const enriched = {
    ...data,
    siret,
    cachedAt: new Date().toISOString(),
  };
  set(KEYS.ENRICH_DATA + siret, enriched);
  return enriched;
}

/**
 * Met a jour partiellement un enrichissement (merge). Use pour ajouter pitch
 * apres generation sans perdre les donnees annuaire/pappers.
 */
export function mergeEnrichissement(siret, patch) {
  const existing = get(KEYS.ENRICH_DATA + siret, {});
  const merged = {
    ...existing,
    ...patch,
    siret,
    cachedAt: new Date().toISOString(),
  };
  set(KEYS.ENRICH_DATA + siret, merged);
  return merged;
}

// ============================================================
// Helpers metier — Notifications intelligentes (Bloc 1)
// ============================================================

/**
 * Structure notification :
 * {
 *   id: string UUID,
 *   type: 'relance_prospect_j5' | 'relance_devis_j7' | 'audit_sans_devis' |
 *         'palier_proche' | 'audit_a_chiffrer' | 'compte_ouvert',
 *   title: string,
 *   message: string,
 *   severity: 'info' | 'warning' | 'success' | 'danger',
 *   navigate: { module: string, context?: object } | null,
 *   dedupKey: string,           // empeche doublons
 *   prospectId?: string,
 *   devisId?: string,
 *   createdAt: string ISO,
 *   triggerAt?: string ISO,     // si dans le futur, notif planifiee (non visible)
 *   read: boolean,
 *   dismissed: boolean,
 *   expiresAt?: string ISO,
 * }
 */

const NOTIF_DEFAULT_EXPIRE_DAYS = 30;

export function getNotifications() {
  const all = get(KEYS.NOTIFICATIONS, []);
  const nowMs = Date.now();
  return all.filter((n) => !n.dismissed && (!n.triggerAt || new Date(n.triggerAt).getTime() <= nowMs));
}

export function getAllNotificationsRaw() {
  return get(KEYS.NOTIFICATIONS, []);
}

export function addNotification(notif) {
  if (!notif || !notif.dedupKey) return null;
  const list = getAllNotificationsRaw();
  if (list.some((n) => n.dedupKey === notif.dedupKey && !n.dismissed)) return null;
  const expireDays = notif.expireDays ?? NOTIF_DEFAULT_EXPIRE_DAYS;
  const enriched = {
    id: crypto.randomUUID ? crypto.randomUUID() : `n-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    severity: 'info',
    read: false,
    dismissed: false,
    createdAt: new Date().toISOString(),
    expiresAt: new Date(Date.now() + expireDays * 86400000).toISOString(),
    ...notif,
  };
  list.unshift(enriched);
  set(KEYS.NOTIFICATIONS, list.slice(0, 200));
  return enriched;
}

export function markNotificationRead(id) {
  const list = getAllNotificationsRaw();
  const idx = list.findIndex((n) => n.id === id);
  if (idx === -1) return false;
  list[idx] = { ...list[idx], read: true };
  set(KEYS.NOTIFICATIONS, list);
  return true;
}

export function markAllNotificationsRead() {
  const list = getAllNotificationsRaw().map((n) => ({ ...n, read: true }));
  set(KEYS.NOTIFICATIONS, list);
}

export function dismissNotification(id) {
  const list = getAllNotificationsRaw();
  const idx = list.findIndex((n) => n.id === id);
  if (idx === -1) return false;
  list[idx] = { ...list[idx], dismissed: true };
  set(KEYS.NOTIFICATIONS, list);
  return true;
}

export function getUnreadCount() {
  return getNotifications().filter((n) => !n.read).length;
}

export function cleanExpiredNotifications() {
  const nowMs = Date.now();
  const list = getAllNotificationsRaw();
  const kept = list.filter((n) => !n.expiresAt || new Date(n.expiresAt).getTime() > nowMs);
  if (kept.length !== list.length) {
    set(KEYS.NOTIFICATIONS, kept);
  }
  return list.length - kept.length;
}

// ============================================================
// Helpers metier — Historique mails generes (Module 6)
// ============================================================

export function getMailsGeneres() {
  return get(KEYS.MAILS_GENERES, []);
}

export function addMailGenere(mail) {
  const list = getMailsGeneres();
  const enriched = {
    id: crypto.randomUUID ? crypto.randomUUID() : `m-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    createdAt: new Date().toISOString(),
    ...mail,
  };
  list.unshift(enriched);
  set(KEYS.MAILS_GENERES, list.slice(0, 20));
  return enriched;
}

// ============================================================
// Helpers metier — Commandes (SPEC-04, socle du reassort predictif)
// ============================================================

/**
 * Structure commande :
 * {
 *   id: string (UUID v4),
 *   prospectId: string,
 *   date: string (ISO),                 // date de confirmation de la commande
 *   lignes: [{ ref_interne, designation?, quantite, pa_ht? }],
 *   source: 'devis_accepte' | 'reassort_confirme' | 'manuel',
 *   devisId: string | null,             // si issue d'un devis accepte (dedup)
 *   totalHT: number | null,             // snapshot chiffrage si connu
 * }
 *
 * C'est l'evenement d'achat REEL (Option B SPEC-04) : le reassort se calcule
 * depuis la DERNIERE commande, jamais depuis la date d'ouverture du compte.
 * Alimente automatiquement par : (1) passage devis -> accepte, (2) bouton
 * "Commande confirmee" sur une alerte de reassort. Pas de migration
 * retroactive des comptes anterieurs (pas de donnees fiables).
 */

export function listCommandes() {
  return get(KEYS.COMMANDES, []);
}

export function listCommandesByProspect(prospectId) {
  return listCommandes().filter((c) => c.prospectId === prospectId);
}

/**
 * Derniere commande d'un prospect (date max), ou null.
 */
export function getDerniereCommande(prospectId) {
  const list = listCommandesByProspect(prospectId);
  if (!list.length) return null;
  return list.reduce((a, b) => (String(a.date) >= String(b.date) ? a : b));
}

/**
 * Ajoute une commande. Idempotent sur devisId : si une commande existe deja
 * pour ce devis (re-clic "Accepte"), retourne l'existante sans dupliquer.
 */
export function addCommande({ prospectId, date, lignes, source, devisId = null, totalHT = null }) {
  if (!prospectId || !Array.isArray(lignes) || lignes.length === 0) {
    console.warn('[storage] addCommande: prospectId et lignes requis');
    return null;
  }
  const list = listCommandes();
  if (devisId) {
    const existing = list.find((c) => c.devisId === devisId);
    if (existing) return existing;
  }
  const enriched = {
    id: (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `c-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    prospectId,
    date: date || new Date().toISOString(),
    lignes: lignes.map((l) => ({
      ref_interne: l.ref_interne,
      designation: l.designation || null,
      quantite: Math.max(1, Number(l.quantite) || 1),
      pa_ht: (l.pa_ht != null && Number.isFinite(Number(l.pa_ht))) ? Number(l.pa_ht) : null,
    })),
    source: source || 'manuel',
    devisId,
    totalHT: (totalHT != null && Number.isFinite(Number(totalHT))) ? Number(totalHT) : null,
  };
  list.push(enriched);
  set(KEYS.COMMANDES, list);
  return enriched;
}

/**
 * Met a jour une commande (ex : enrichissement rendement_pages des lignes
 * depuis D1 apres creation). Merge shallow, id immuable.
 */
export function updateCommande(id, patch) {
  const list = listCommandes();
  const idx = list.findIndex((c) => c.id === id);
  if (idx === -1) return null;
  list[idx] = { ...list[idx], ...patch, id };
  set(KEYS.COMMANDES, list);
  return list[idx];
}

// ============================================================
// Helpers metier — Journal d'interactions + prochaine action (SPEC-05)
// ============================================================

/**
 * Interaction : { id, prospectId, type, date, texte, source, prochaineAction }
 * type   : 'appel' | 'mail' | 'visite' | 'note'
 * source : 'manuel' | 'vocal'
 * prochaineAction : { date: ISO, libelle } | null
 *
 * Le prospect porte lui-meme un champ `prochaineAction` (la plus recente,
 * "en cours" — pas un historique). Chaque interaction qui en fournit une
 * neuve remplace celle du prospect ; le journal (KEYS.INTERACTIONS) garde
 * l'historique complet, y compris les prochaineAction passees/perimees.
 *
 * Regle dure (voir lib/interactions.js) : impossible d'enregistrer une
 * interaction si le prospect n'a ni prochaineAction future existante, ni
 * une fournie dans le meme geste. Pas de blocage retroactif : les prospects
 * deja sans action listables via listProspectsSansProchaineAction().
 */

export function listInteractionsByProspect(prospectId) {
  return get(KEYS.INTERACTIONS, [])
    .filter((i) => i.prospectId === prospectId)
    .sort((a, b) => String(b.date).localeCompare(String(a.date)));
}

/**
 * Enregistre une interaction. Retourne le record cree, ou null (+ log) si
 * la regle "prochaine action obligatoire" n'est pas satisfaite — meme
 * convention que addCommande (pas d'exception, l'appelant verifie le retour).
 */
export function addInteraction({ prospectId, type, date, texte, source, prochaineAction }) {
  if (!prospectId || !texte || !String(texte).trim()) {
    console.warn('[storage] addInteraction: prospectId et texte requis');
    return null;
  }
  const prospect = getProspect(prospectId);
  if (!prospect) {
    console.warn('[storage] addInteraction: prospect introuvable', prospectId);
    return null;
  }

  const resolved = resolveProchaineAction(prospect, prochaineAction);
  if (!resolved.ok) {
    console.warn('[storage] addInteraction: prochaine action obligatoire manquante', prospectId);
    return null;
  }

  const list = get(KEYS.INTERACTIONS, []);
  const record = {
    id: crypto.randomUUID ? crypto.randomUUID() : `i-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    prospectId,
    type: ['appel', 'mail', 'visite', 'note'].includes(type) ? type : 'note',
    date: date || new Date().toISOString(),
    texte: String(texte).trim(),
    source: source === 'vocal' ? 'vocal' : 'manuel',
    prochaineAction: resolved.prochaineAction || prospect.prochaineAction || null,
  };
  list.push(record);
  set(KEYS.INTERACTIONS, list);

  if (resolved.updateProspect) {
    updateProspect(prospectId, { prochaineAction: resolved.prochaineAction });
  }

  return record;
}

/**
 * Prospects sans prochaineAction future — carte Dashboard, jamais un blocage
 * retroactif (cf. discipline "pas de migration qui invente des dates").
 */
export function listProspectsSansProchaineAction() {
  return getProspects().filter((p) => !hasFutureProchaineAction(p));
}

// ============================================================
// Helpers metier — Export / Import backup complet (Bloc 7)
// ============================================================

/**
 * Exporte toutes les cles fg-os:* sous forme d'objet JSON serialisable.
 * Format : { version, exportedAt, app, data: { [key without namespace]: rawWrappedValue } }
 */
export function exportAllData() {
  if (!isAvailable()) return null;
  const data = {};
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const fullKey = window.localStorage.key(i);
      if (!fullKey || !fullKey.startsWith(NAMESPACE)) continue;
      const shortKey = fullKey.slice(NAMESPACE.length);
      const raw = window.localStorage.getItem(fullKey);
      try {
        data[shortKey] = JSON.parse(raw);
      } catch {
        data[shortKey] = raw;
      }
    }
  } catch (err) {
    console.warn('[storage] exportAllData err:', err.message);
  }
  return {
    app: 'fg-os',
    version: STORAGE_VERSION,
    exportedAt: new Date().toISOString(),
    data,
  };
}

// ============================================================
// Helpers metier — Catalogue concurrent Inkya (imprim-encre.com)
// ============================================================

/**
 * Catalogue Inkya importe (prix CLIENT FINAL TTC, comparateur concurrent).
 * Array de { ref_inkya, ean, designation, marque, categorie,
 *            compatible_ou_oem, prix_ttc, prix_ht_estime, dispo, url }.
 */
export function getInkyaCatalogue() {
  return get(KEYS.INKYA_CATALOGUE, []);
}

export function setInkyaCatalogue(refs) {
  const list = Array.isArray(refs) ? refs : [];
  set(KEYS.INKYA_CATALOGUE, list);
  set(KEYS.INKYA_META, { importedAt: new Date().toISOString(), count: list.length });
  return list.length;
}

export function getInkyaMeta() {
  return get(KEYS.INKYA_META, { importedAt: null, count: 0 });
}

export function clearInkyaCatalogue() {
  remove(KEYS.INKYA_CATALOGUE);
  remove(KEYS.INKYA_META);
}

/**
 * Importe un backup. Remplace integralement les donnees fg-os:* existantes.
 * Retourne { ok, imported, skipped, error? }.
 */
export function importAllData(backup) {
  if (!isAvailable()) return { ok: false, error: 'localStorage indisponible' };
  if (!backup || typeof backup !== 'object' || backup.app !== 'fg-os' || !backup.data) {
    return { ok: false, error: 'Format backup invalide (attendu app="fg-os", data={...})' };
  }
  clearAll();
  let imported = 0;
  let skipped = 0;
  for (const [shortKey, value] of Object.entries(backup.data)) {
    try {
      window.localStorage.setItem(NAMESPACE + shortKey, JSON.stringify(value));
      imported++;
    } catch {
      skipped++;
    }
  }
  return { ok: true, imported, skipped };
}
